import { escapeXPathLiteral } from "./billingCodesPaste";

/**
 * Pure DOM-reading / judgment helpers for Practice Fusion's ICD-10 diagnosis picker.
 *
 * Everything here operates on HTML strings returned by the extension's shipped `getDOM`
 * action — no I/O, no timers, no extension coupling — so the clinical-safety core (toggle
 * safety, tri-state selection reads, commit grading, auto-map guards) is unit-testable.
 * The orchestration that feeds these functions lives in pfPickerDriver.js.
 */

export const PF_ROW_SELECTOR = 'li[role="option"]';
export const PF_CODE_SELECTOR = ".superbill__diagnosis-result-code h4";
export const PF_REFINEMENT_SELECTOR = '[data-element="option-has-refinement"]';

/** Normalize `getDOM`'s response shapes into an array of HTML strings.
 * The content script answers with a single `{html}`, but the background route can hand back
 * an array of per-frame entries — useEHR.js's resolveSelectOption handles both, and so must
 * this. Rows from EVERY entry are merged (a frame that answered is real DOM, not a duplicate
 * to be discarded). */
export function toHtmlList(htmlOrHtmls) {
  const entries = Array.isArray(htmlOrHtmls) ? htmlOrHtmls : [htmlOrHtmls];
  const out = [];
  for (const entry of entries) {
    if (typeof entry === "string") {
      if (entry !== "") out.push(entry);
    } else if (entry && typeof entry.html === "string" && entry.html !== "") {
      out.push(entry.html);
    }
  }
  return out;
}

function parseDocs(htmlOrHtmls) {
  return toHtmlList(htmlOrHtmls).map((html) => new DOMParser().parseFromString(html, "text/html"));
}

function readCode(el) {
  const codeEl = el.querySelector(PF_CODE_SELECTOR);
  if (!codeEl) return { codeEl: null, code: null };
  return { codeEl, code: (codeEl.textContent || "").trim() };
}

function readDescription(codeEl) {
  // PF renders the code cell and the description cell as sibling `.item` blocks; the full
  // (untruncated) text only exists in the description cell's title attribute.
  const itemEl = codeEl && codeEl.closest ? codeEl.closest(".item") : null;
  const descEl = itemEl ? itemEl.nextElementSibling : null;
  if (!descEl) return "";
  return (descEl.getAttribute("title") || descEl.textContent || "").trim();
}

function readRefinement(el) {
  // The icon div is always present in a row; the `invisible` class means "not required".
  const icon = el.querySelector(PF_REFINEMENT_SELECTOR);
  return !!(icon && !icon.classList.contains("invisible"));
}

function rowFromElement(el) {
  const { codeEl, code } = readCode(el);
  if (!codeEl) return null;
  return {
    code,
    description: readDescription(codeEl),
    selected: el.getAttribute("aria-selected") === "true",
    hasAriaSelected: el.hasAttribute("aria-selected"),
    needsRefinement: readRefinement(el),
  };
}

/**
 * Every rendered DIAGNOSIS row in the supplied HTML. Rows without the diagnosis code cell
 * are skipped, so option lists belonging to other composable-select widgets (med search,
 * encounter-type dropdowns) can neither pollute the code set nor inflate the row count that
 * the teardown discriminator keys off.
 */
export function parsePickerRows(htmlOrHtmls) {
  const rows = [];
  for (const doc of parseDocs(htmlOrHtmls)) {
    for (const el of doc.querySelectorAll(PF_ROW_SELECTOR)) {
      const row = rowFromElement(el);
      if (row) rows.push(row);
    }
  }
  return rows;
}

/**
 * The state of the element a selector RESOLVED TO, not of any row inside it.
 *
 * This distinction is the whole point of v1's one-time aria-selected assertion: an
 * optionSelectorTemplate anchored on a wrapper instead of the `li[role="option"]` itself
 * still contains a row, so a descendant-based read would report a perfectly healthy
 * `hasAriaSelected` for a mis-anchored template and the toggle-safety check below it would
 * be permanently, silently "false" — i.e. always CLICK, on a toggle that deletes diagnoses.
 * `selected` / `hasAriaSelected` therefore come from the root element only; the code,
 * description and refinement icon are read from its subtree exactly as v1 did.
 */
export function parseRootRow(htmlOrHtmls) {
  for (const doc of parseDocs(htmlOrHtmls)) {
    const root = doc.body ? doc.body.firstElementChild : null;
    if (!root) continue;
    const { codeEl, code } = readCode(root);
    return {
      code: codeEl ? code : null,
      description: codeEl ? readDescription(codeEl) : "",
      selected: root.getAttribute("aria-selected") === "true",
      hasAriaSelected: root.hasAttribute("aria-selected"),
      needsRefinement: readRefinement(root),
      isOption: root.getAttribute("role") === "option",
    };
  }
  return null;
}

/**
 * The picker's currently-selected code set plus the number of rendered diagnosis rows.
 *
 * `rowCount` is the teardown discriminator: 0 means the picker itself is gone (re-mount,
 * navigation, list closed) and the code set says nothing about the chart — whereas
 * rowCount > 0 with a previously-selected code absent is a genuine de-selection, even in
 * the common single-code case that an "all codes vanished" heuristic cannot tell apart.
 * Codes are NOT de-duplicated: two rows reporting the same selected code is a picker state
 * we have never observed, and letting it grade as a set mismatch fails closed.
 */
export function snapshotFromRows(rows) {
  if (!Array.isArray(rows)) return null;
  return {
    codes: rows.filter((r) => r.selected).map((r) => r.code),
    rowCount: rows.length,
  };
}

export function computeDelta(before, after) {
  const beforeCodes = (before && before.codes) || [];
  const afterCodes = (after && after.codes) || [];
  const beforeSet = new Set(beforeCodes);
  const afterSet = new Set(afterCodes);
  return {
    added: afterCodes.filter((c) => !beforeSet.has(c)),
    removed: beforeCodes.filter((c) => !afterSet.has(c)),
  };
}

/** Stable signature of a delta, for the "two consecutive agreeing reads" poll rule. */
export function deltaSignature(before, after) {
  const d = computeDelta(before, after);
  return d.added.join(",") + "|" + d.removed.join(",");
}

/**
 * Graded judgment of one commit. Ordering is load-bearing — each rung has strictly more
 * knowledge than the one below it:
 *
 *  (0) An unreadable snapshot, or zero rendered diagnosis rows, determines the outcome on
 *      its own: the snapshot says nothing about the chart, so report "unknown" and NEVER
 *      infer a removal from a teardown.
 *  (a) Anything removed is the one outcome where the automation has POSITIVE knowledge it
 *      deleted a previously-recorded diagnosis. It gets its own reason (and carries the
 *      codes) so it can never render to the provider as the benign "missed" string.
 *  (b) Anything added other than exactly {code} — including when `selected` is false, i.e.
 *      the mis-landed click where the intended row never registered but a neighbour did.
 *      An EMPTY added set is not a mismatch; that is the benign no-op judged in (c).
 *  (c) Only a clean delta may degrade to "checkbox_did_not_select". This also covers the
 *      contradictory added===[code] && !selected read: matched still requires the row's own
 *      signal, so it stays a visible miss.
 */
export function judgeCommit({ before, after, code, selected }) {
  if (!before || before.rowCount === 0 || !after || after.rowCount === 0) {
    return { matched: false, reason: "commit_delta_unknown" };
  }
  const { added, removed } = computeDelta(before, after);
  if (removed.length > 0) {
    return { matched: false, reason: "commit_removed_existing_code", removedCodes: removed };
  }
  if (added.length > 0 && (added.length !== 1 || added[0] !== code)) {
    return { matched: false, reason: "commit_set_mismatch" };
  }
  if (added.length === 0 || !selected) {
    return { matched: false, reason: "checkbox_did_not_select" };
  }
  return { matched: true };
}

/**
 * Scan rendered rows for a billable child of a parent code the picker refused to match
 * exactly (e.g. requested "M54.5", rows show "M54.50/.51/.59 … unspecified"). Guards ported
 * one-for-one from v1 (itself a port of AdvancedMD's findUnspecifiedChildInPicker):
 *   1. parent must look like an ICD-10 prefix (^[A-Z][0-9][0-9A-Z], len >= 3)
 *   2. alphanumeric + '.' only (XPath-injection + data-corruption defense)
 *   3. must contain '.' — subcategory parents only, never chapter categories
 *   4. child strictly longer than parent, parent a strict prefix, deduped
 *   5. prefer /\bunspecified\b/i in the description; else first candidate
 *   6. the candidate's OWN code text must also pass the charset guard — #2 only validated
 *      the parent, and the suffix beyond the parent prefix comes straight out of the DOM
 *   7. rows whose refinement icon is visible are skipped — auto-map stays on the simplest,
 *      checkbox-committable candidates (a substitute that ALSO needed the refinement-panel
 *      flow would stack two fallbacks on one code; conservative by design)
 * Returns { code, description, matchType, candidateCount } or null.
 */
export function findUnspecifiedChild(rows, parentCode) {
  if (!parentCode || typeof parentCode !== "string") return null;
  if (parentCode.length < 3) return null;
  if (!/^[A-Z][0-9][0-9A-Z]/.test(parentCode)) return null;
  if (/[^A-Z0-9.]/i.test(parentCode)) return null;
  if (!parentCode.includes(".")) return null;
  if (!Array.isArray(rows)) return null;

  const candidates = [];
  const seen = new Set();
  for (const row of rows) {
    const codeText = row && typeof row.code === "string" ? row.code : "";
    if (!codeText) continue;
    if (/[^A-Z0-9.]/i.test(codeText)) continue;
    if (codeText === parentCode) continue;
    if (codeText.length <= parentCode.length) continue;
    if (codeText.indexOf(parentCode) !== 0) continue;
    if (seen.has(codeText)) continue;
    if (row.needsRefinement) continue;
    seen.add(codeText);
    candidates.push({ code: codeText, description: row.description || "" });
  }

  if (candidates.length === 0) return null;
  const unspecified = candidates.find((c) => /\bunspecified\b/i.test(c.description));
  const chosen = unspecified || candidates[0];
  return {
    code: chosen.code,
    description: chosen.description,
    matchType: unspecified ? "unspecified" : "first_child",
    candidateCount: candidates.length,
  };
}

/**
 * The results container of the widget being typed into: the NEAREST ancestor of the search
 * input that contains rendered option rows. This is the pure-XPath equivalent of v1's
 * pfMarkResultsScope walk, including its refusal to degrade to body/documentElement — an
 * unscoped, document-wide query can be answered by a duplicate/stale composable-select
 * instance elsewhere on the page (the spec's own caveat).
 */
export function buildResultsContainerXPath(searchInputXPath) {
  if (typeof searchInputXPath !== "string" || searchInputXPath.trim() === "") return null;
  // The input's ancestry is NOT usable as a scope on Practice Fusion: Ember portals the
  // results dropdown far away from the input (live-confirmed 2026-08-20 — the nearest common
  // ancestor is the page-level `div.charts.outlet`, 21 hops up, which also hosts every OTHER
  // search widget's option rows, so an ancestor-derived scope is either unresolvable or
  // page-wide). The widget-true scope is the diagnosis rows' own data-element prefix —
  // "select-diagnoses-option-N" is specific to the diagnosis picker — so the container is
  // those rows' direct parent, wherever PF portals it. The searchInput argument is retained
  // only as the call-site validity gate (a template with no search input has no widget).
  return '(//li[@role="option"][starts-with(@data-element,"select-diagnoses-option")]/..)[1]';
}

/** Whether the rows inside a container HTML belong to more than one result list. v1 refused
 * such a scope outright: it means the chosen ancestor spans two composable-select widgets,
 * so pinning to it is not actually scoping to the one being typed into. */
export function containerSpansMultipleLists(htmlOrHtmls) {
  const groups = new Set();
  for (const doc of parseDocs(htmlOrHtmls)) {
    for (const el of doc.querySelectorAll(PF_ROW_SELECTOR)) {
      groups.add(el.closest('[role="listbox"]') || el.parentElement);
    }
  }
  return groups.size > 1;
}

/** A result row addressed by its exact displayed code, scoped to one widget's container.
 * Used by the auto-map path, which cannot use the template-authored optionSelectors (the
 * child code was never in the requested list). The code text is XPath-literal-escaped, and
 * has already passed findUnspecifiedChild's charset guard. */
export function buildRowXPathForCode(containerXPath, codeText) {
  const scope = typeof containerXPath === "string" && containerXPath !== "" ? containerXPath : "";
  // starts-with(@data-element,...) pins the row to the DIAGNOSIS picker even when the
  // resolver searches document-wide (CDP pierce) — the code-text predicate alone could match
  // a same-code row in another widget's portaled list.
  return (
    scope +
    '//li[@role="option"][starts-with(@data-element,"select-diagnoses-option")]' +
    '[.//div[contains(@class,"superbill__diagnosis-result-code")]//h4[normalize-space(text())=' +
    escapeXPathLiteral(codeText) +
    "]]"
  );
}

/** The clickable checkbox label inside an already-resolved row. Scoped INSIDE the row's own
 * selector so a trusted click can never land on a neighbouring row's checkbox. */
export function buildRowCheckboxLabelXPath(rowXPath) {
  return rowXPath + '//*[contains(concat(" ", normalize-space(@class), " "), " check-box__label ")]';
}

/** Fallback for a row that renders no label wrapper — mirrors v1's `.check-box__label ||
 * input[type="checkbox"]` ordering. */
export function buildRowCheckboxInputXPath(rowXPath) {
  return rowXPath + '//input[@type="checkbox"]';
}

// ---------------------------------------------------------------------------------------------
// Refinement panel (captured live 2026-08-21). A row whose filter icon is visible does not
// commit from its checkbox — Practice Fusion opens a refinement panel instead. The panel's
// "Search match" section offers the ORIGINAL code as its own committable entry
// (data-element="original-search-diagnosis"), so the requested code is committed exactly,
// never a substitute. Panel rows use a different code-cell scheme from the main results:
// [data-element="dx-search-result-code"], no superbill__diagnosis-result-code class.
// ---------------------------------------------------------------------------------------------

/** The refinement (filter) icon inside an already-resolved row — the explicit popover
 * trigger (role=button, aria-haspopup=dialog), a safer open-the-panel click target than the
 * row body, whose click semantics differ between refinable and plain rows. */
export function buildRefinementIconXPath(rowXPath) {
  return rowXPath + '//*[@data-element="option-has-refinement"]';
}

/** The panel's "Search match" entry for exactly the requested code. Document-wide on purpose:
 * the panel is portaled like every other composable-select surface, and
 * original-search-diagnosis exists only while a refinement panel is open, one entry per
 * panel. The code predicate keeps a stale panel for a DIFFERENT code from ever matching. */
export function buildRefinementOriginalXPath(codeText) {
  return (
    '//li[@role="option"][@data-element="original-search-diagnosis"]' +
    '[.//*[@data-element="dx-search-result-code"][normalize-space(text())=' +
    escapeXPathLiteral(codeText) +
    "]]"
  );
}

/** Parse the panel's Search-match entry from a root read of buildRefinementOriginalXPath.
 * Returns `{ code }` (null when the payload holds no such entry). */
export function parseRefinementOriginalRow(htmlOrHtmls) {
  for (const doc of parseDocs(htmlOrHtmls)) {
    const li = doc.querySelector('li[role="option"][data-element="original-search-diagnosis"]');
    if (!li) continue;
    const codeEl = li.querySelector('[data-element="dx-search-result-code"]');
    return { code: codeEl ? (codeEl.textContent || "").trim() : null };
  }
  return null;
}
